import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  FormControlLabel,
  Checkbox,
  Box,
  CircularProgress,
} from '@mui/material';
import { AppButton, AppTextField } from '../../../components/design-system';
import { WarningAmber as WarningIcon } from '@mui/icons-material';
import { opsDevicesService } from '../../../services/OpsDevicesService';

interface RetireDeviceModalProps {
  open: boolean;
  serialNumber: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function RetireDeviceModal({
  open,
  serialNumber,
  onClose,
  onSuccess,
}: RetireDeviceModalProps) {
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setReason('');
    setConfirmed(false);
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError('A reason is required.');
      return;
    }
    if (!confirmed) {
      setError('Please confirm the action by checking the box.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await opsDevicesService.retireDevice(serialNumber, reason.trim());
      onSuccess();
      handleClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Retire failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningIcon color="error" />
        Retire Device
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            <strong>Serial:</strong> {serialNumber}
          </Typography>
        </Box>

        <Alert severity="error" sx={{ mb: 2 }}>
          This permanently retires the device from service. It cannot be reallocated or
          redeployed without provider intervention.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <AppTextField
          label="Reason for retiring *"
          fullWidth
          multiline
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g., Hardware failure, end of life, damaged beyond repair"
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Checkbox checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          }
          label="I confirm this device should be permanently retired."
        />
      </DialogContent>
      <DialogActions>
        <AppButton onClick={handleClose} disabled={submitting}>
          Cancel
        </AppButton>
        <AppButton
          variant="contained"
          color="error"
          onClick={handleSubmit}
          disabled={submitting || !confirmed || !reason.trim()}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          Retire Device
        </AppButton>
      </DialogActions>
    </Dialog>
  );
}
